import { useRouter } from 'expo-router';
import type { ReactNode } from 'react';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { PartnerSubscription } from '@/api/partners/subscriptions/types';
import { formatInOriginalOffset } from '@/core/utils';
import { Card, Text } from '@/ui';
import { Chip } from '@/ui/chip';
import { Icon } from '@/ui/icon';
import { Person } from '@/ui/icons';
import { Location } from '@/ui/icons/location';

import { PickupTypeChip, SubscriptionStatusChip } from './reservation-parts';
import { getRenewalPeriodLabel } from './status-label';

const InfoRow = ({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) => {
  const { styles, theme } = useStyles(stylesheet);

  return (
    <View style={styles.infoRow}>
      <Icon
        type="naked"
        icon={icon}
        fillColor={theme.colors.icon.color}
        width={16}
        height={16}
        style={styles.infoIcon}
      />

      <View style={styles.infoContent}>{children}</View>
    </View>
  );
};

/** Mobile list card for a subscription. */
export const SubscriptionCard = ({
  subscription,
}: {
  subscription: PartnerSubscription;
}) => {
  const { styles } = useStyles(stylesheet);
  const router = useRouter();
  const { car, pickup, pickupType } = subscription;

  return (
    <Pressable
      onPress={() => {
        router.push(`/partner/reservations/subscriptions/${subscription.id}`);
      }}
    >
      <Card>
        <View style={styles.header}>
          <Text type="title2">{`${car.year} ${car.make} ${car.model} (${car.plate})`}</Text>

          <View style={styles.chips}>
            <SubscriptionStatusChip status={subscription.status} />

            {pickupType ? (
              <PickupTypeChip isDelivery={pickupType === 'delivery'} />
            ) : null}

            <Chip text={car.vin} />
          </View>
        </View>

        <View style={styles.body}>
          <InfoRow icon={<Person />}>
            <Text type="bodyRegular">Customer</Text>

            <Text type="bodyRegular" style={styles.secondaryText}>
              {`${subscription.customer.firstName} ${subscription.customer.lastName}`}
            </Text>
          </InfoRow>

          {pickup ? (
            <InfoRow icon={<Location />}>
              <Text type="bodyRegular">
                {pickupType === 'delivery'
                  ? 'Delivery location'
                  : 'Pickup location'}
              </Text>

              <Text type="bodyRegular" style={styles.secondaryText}>
                {formatInOriginalOffset(pickup.time, 'MMM dd yyyy - hh:mm a')}
              </Text>

              <Text type="bodyRegular" style={styles.secondaryText}>
                {pickup.address}
              </Text>
            </InfoRow>
          ) : null}
        </View>

        <View style={styles.footer}>
          <Text type="caption1" style={styles.secondaryText}>
            Renewal cadence
          </Text>

          <Text type="caption1">
            {getRenewalPeriodLabel(subscription.renewalPeriod)}
          </Text>
        </View>
      </Card>
    </Pressable>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  header: {
    padding: 16,
    gap: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  chips: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  body: {
    padding: 16,
    gap: 16,
  },
  infoRow: {
    flexDirection: 'row',
    gap: 8,
  },
  infoIcon: {
    width: 40,
    height: 40,
  },
  infoContent: {
    flex: 1,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
  },
  secondaryText: {
    color: theme.colors.text.secondary,
  },
}));
